/**
 * Urgency Engine - Deep Module tính toán cấp độ khẩn cấp (Dual Dynamic Urgency Engine)
 * Kết hợp gán nhãn thủ công (URGENT / HIGH / NORMAL / LOW) + Tự động nâng cấp cảnh báo theo Deadline (<= 48h hoặc Quá hạn)
 * Bảo vệ tuyệt đối: Đơn / Lệnh đã COMPLETED hoặc CANCELLED không bao giờ bị báo đỏ.
 */

export type UrgencyLevel = 'CRITICAL_URGENT' | 'HIGH_WARNING' | 'APPROACHING' | 'NORMAL';

export interface UrgencyEvaluation {
  level: UrgencyLevel;
  isUrgent: boolean;
  isOverdue: boolean;
  diffDays: number | null;
  badgeLabel: string;
  badgeColor: string;
  borderLeftClass: string;
  rowBgClass: string;
  iconType: 'flame' | 'zap' | 'clock' | null;
  tooltipText: string;
}

export function evaluateUrgency(options: {
  priority?: string | null;
  deadline?: string | Date | null;
  status?: string | null;
}): UrgencyEvaluation {
  const { priority, deadline, status } = options;

  // Rule 1: Exemption for completed / cancelled / closed statuses
  const normalizedStatus = (status || '').toUpperCase();
  const isExempt = [
    'COMPLETED',
    'DELIVERED',
    'INVOICED',
    'CLOSED',
    'CANCELLED',
  ].includes(normalizedStatus);

  const normPriority = (priority || 'NORMAL').toUpperCase();

  if (isExempt) {
    return {
      level: 'NORMAL',
      isUrgent: false,
      isOverdue: false,
      diffDays: null,
      badgeLabel:
        normPriority === 'URGENT'
          ? 'Hỏa tốc (Đã xong)'
          : normPriority === 'HIGH'
          ? 'Ưu tiên (Đã xong)'
          : 'Bình thường',
      badgeColor:
        'bg-slate-100 text-slate-600 border-slate-200',
      borderLeftClass: '',
      rowBgClass: '',
      iconType: null,
      tooltipText: 'Đơn hàng / Lệnh sản xuất đã hoàn tất hoặc đóng',
    };
  }

  // Calculate diffDays
  let diffDays: number | null = null;
  if (deadline) {
    const deadlineDate = new Date(deadline);
    if (!isNaN(deadlineDate.getTime())) {
      const now = new Date();
      const msPerDay = 24 * 60 * 60 * 1000;
      diffDays = Math.ceil((deadlineDate.getTime() - now.getTime()) / msPerDay);
    }
  }

  const isPriorityUrgent = normPriority === 'URGENT';
  const isPriorityHigh = normPriority === 'HIGH';
  const isOverdue = diffDays !== null && diffDays < 0;
  const isNearDue = diffDays !== null && diffDays >= 0 && diffDays <= 2; // within 48h
  const isApproaching = diffDays !== null && diffDays > 2 && diffDays <= 5; // 3 to 5 days

  // Level 1: CRITICAL_URGENT
  if (isPriorityUrgent || isOverdue) {
    let label = 'Hỏa tốc';
    let tooltip = 'Đơn hàng / LSX mức độ Hỏa tốc';
    if (isOverdue) {
      label = `Quá hạn (${Math.abs(diffDays!)} ngày)`;
      tooltip = `Đã quá hạn giao hàng ${Math.abs(diffDays!)} ngày! Cần xử lý khẩn cấp!`;
    } else if (isPriorityUrgent && diffDays !== null) {
      label = diffDays <= 0 ? 'Hỏa tốc (Hạn gấp)' : `Hỏa tốc (Còn ${diffDays} ngày)`;
      tooltip = `Lệnh Hỏa tốc - Còn ${diffDays} ngày giao hàng`;
    }

    return {
      level: 'CRITICAL_URGENT',
      isUrgent: true,
      isOverdue,
      diffDays,
      badgeLabel: label,
      badgeColor:
        'bg-red-600 text-white border-red-700 font-extrabold shadow-xs',
      borderLeftClass: 'border-l-4 !border-l-red-600',
      rowBgClass:
        'apv-urgent-row !bg-[#ffe4e6] hover:!bg-[#fecdd3]',
      iconType: 'flame',
      tooltipText: tooltip,
    };
  }

  // Level 2: HIGH_WARNING
  if (isPriorityHigh || isNearDue) {
    let label = 'Ưu tiên cao';
    let tooltip = 'Đơn hàng / LSX ưu tiên cao';
    if (isNearDue) {
      label = diffDays === 0 ? 'Hạn hôm nay' : `Cận hạn (${diffDays} ngày)`;
      tooltip = `Hạn giao cận kề (${diffDays === 0 ? 'hôm nay' : `còn ${diffDays} ngày`})! Cần đẩy nhanh tiến độ!`;
    } else if (isPriorityHigh && diffDays !== null) {
      label = `Ưu tiên (${diffDays} ngày)`;
      tooltip = `Mức ưu tiên cao - Còn ${diffDays} ngày`;
    }

    return {
      level: 'HIGH_WARNING',
      isUrgent: true,
      isOverdue: false,
      diffDays,
      badgeLabel: label,
      badgeColor:
        'bg-amber-100 text-amber-800 border-amber-300 font-medium',
      borderLeftClass: 'border-l-4 border-l-amber-500',
      rowBgClass:
        'bg-amber-50/30 hover:bg-amber-50/50',
      iconType: 'zap',
      tooltipText: tooltip,
    };
  }

  // Level 3: APPROACHING
  if (isApproaching) {
    return {
      level: 'APPROACHING',
      isUrgent: false,
      isOverdue: false,
      diffDays,
      badgeLabel: `Còn ${diffDays} ngày`,
      badgeColor:
        'bg-blue-50 text-blue-700 border-blue-200',
      borderLeftClass: 'border-l-2 border-l-blue-400',
      rowBgClass: '',
      iconType: 'clock',
      tooltipText: `Hạn giao còn ${diffDays} ngày`,
    };
  }

  // Level 4: NORMAL
  return {
    level: 'NORMAL',
    isUrgent: false,
    isOverdue: false,
    diffDays,
    badgeLabel: normPriority === 'LOW' ? 'Ưu tiên thấp' : 'Bình thường',
    badgeColor:
      'bg-slate-100 text-slate-600 border-slate-200',
    borderLeftClass: '',
    rowBgClass: '',
    iconType: null,
    tooltipText:
      diffDays !== null ? `Hạn giao còn ${diffDays} ngày` : 'Tiến độ bình thường',
  };
}
